import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react';
import { FaFilter } from 'react-icons/fa';
import styled from 'styled-components';
import CollapsibleFilters from '../CollapsibleFilters';
import UpgradeDiamond from '../UpgradeDiamond';
import { theme } from '../../../theme';

export type FilterOptionType = {
  label: string;
  value: string;
  isText?: boolean;
  isNumber?: boolean;
  isDate?: boolean;
  isDateRange?: boolean;
  startDate?: string;
  endDate?: string;
  options?: { label: string; value: string | boolean }[];
  query?: {
    query: any;
    result: string;
    resultOptionFunction?: (data: any) => { label: string; value: string };
    variables: any;
    searchText?: string;
    setSearchText?: Dispatch<SetStateAction<string>>;
  };
};

export type ViewOptionType = {
  label: string;
  name: string;
  /** Greyed out, with an upgrade diamond. Still selectable. */
  locked?: boolean;
};

export type FilterStateType = {
  filterType?: string | null;
  name?: string;
};

interface NavProps {
  disableFilter?: boolean;
  filterChangeHandler?: (args: { value: string | Date | number | null }, stateKey?: string | undefined) => void;
  filterOptions?: FilterOptionType[];
  filterState?: FilterStateType;
  filterSearchTerm?: string;
  setFilterSearchTerm?: Dispatch<SetStateAction<string>>;
  setView: (name: string) => void;
  viewOptions: ViewOptionType[];
  currentView: string;
  /** Buttons shown at the right of the bar, before the filter icon. */
  actions?: ReactNode;
}

type MenuProps = {
  highlighted: boolean;
  locked?: boolean;
};

const Wrapper = styled.div`
  background-color: ${theme.colors.transparentPrimary};
  color: ${theme.colors.white};
  display: flex;
  flex-direction: row;
  font-weight: 400;
  padding: 10px 0;
  top: 0;
  width: 100%;

  animation: fadeIn 1s;
  -webkit-animation: fadeIn 1s;
  -moz-animation: fadeIn 1s;
  -o-animation: fadeIn 1s;
  -ms-animation: fadeIn 1s;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-moz-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-webkit-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-o-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-ms-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: row;
  flex-basis: 70%;
  justify-content: flex-start;
`;

// Icons are centred on the tab labels' line rather than sitting at the top of the bar.
const FilterSection = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-basis: 30%;
  font-size: 1.4em;
  justify-content: flex-end;
  padding: 0 35px;
`;

const CollapsibleFilterWrapper = styled.div`
  background-color: ${theme.colors.transparentPrimary};
  color: ${theme.colors.white};
  display: flex;
  flex-direction: row;
  font-weight: 400;
  justify-content: flex-end;
  padding: 10px 0;
  top: 0;
  width: 100%;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-moz-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-webkit-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-o-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-ms-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const MenuItem = styled.div<MenuProps>`
  align-items: center;
  border-bottom: ${props =>
    props.highlighted ? `3px solid ${props.locked ? 'rgba(255,255,255,0.35)' : '#FFF'}` : 'none'};
  color: ${props => (props.locked ? 'rgba(255,255,255,0.45)' : 'inherit')};
  cursor: pointer;
  display: inline-flex;
  padding: 10px 25px 10px 15px;
`;

const MenuLabel = styled.span`
  align-items: center;
  display: inline-flex;
`;

const ActionSlot = styled.span`
  align-items: center;
  display: inline-flex;
  margin-right: 24px;
`;

const DiamondSlot = styled.span`
  display: inline-flex;
  margin-left: 8px;
`;

const TabbedFilterNav: FC<NavProps> = props => {
  const {
    disableFilter,
    filterChangeHandler,
    filterOptions,
    filterSearchTerm,
    filterState,
    setFilterSearchTerm,
    viewOptions,
  } = props;
  const [expandFilter, setExpandFilter] = useState<boolean>(false);

  return (
    <>
      <Wrapper>
        <MenuSection>
          {viewOptions.map((type, index) => {
            return (
              <MenuItem
                key={index}
                onClick={() => props.setView(type.name)}
                highlighted={type.name === props.currentView}
                locked={type.locked}
              >
                <MenuLabel>
                  {type.label}
                  {type.locked ? (
                    <DiamondSlot>
                      <UpgradeDiamond invert />
                    </DiamondSlot>
                  ) : null}
                </MenuLabel>
              </MenuItem>
            );
          })}
        </MenuSection>
        {(props.actions || !disableFilter) && (
          <FilterSection>
            {props.actions ? <ActionSlot>{props.actions}</ActionSlot> : null}
            {!disableFilter && (
              <FaFilter style={{ cursor: 'pointer' }} onClick={() => setExpandFilter(!expandFilter)} />
            )}
          </FilterSection>
        )}
      </Wrapper>
      {!disableFilter && expandFilter && (
        <CollapsibleFilterWrapper>
          {filterChangeHandler && filterOptions && (
            <CollapsibleFilters
              changeHandler={filterChangeHandler}
              filterOptions={filterOptions}
              filterState={filterState}
              searchName={filterSearchTerm}
              setSearchName={setFilterSearchTerm}
            />
          )}
        </CollapsibleFilterWrapper>
      )}
    </>
  );
};

export default TabbedFilterNav;
